// src/lib/juliette-sentences.ts
//
// Découpage du flux de réponse de Juliette en segments envoyés au TTS.
//
// POURQUOI UN SEUIL PROGRESSIF (2026-08-10)
// -----------------------------------------
// Chaque segment part dans un appel ElevenLabs séparé. Un segment court n'a
// pas de quoi porter une ligne mélodique : le modèle le prononce à plat et le
// termine comme une fin de paragraphe. Mesuré en production avant correctif :
// 78 caractères par segment en moyenne (110 tours), soit une douzaine de mots.
// Symptôme rapporté à l'oreille : « l'intonation est bizarre ».
//
// Mais allonger tous les segments retarderait le PREMIER son, et c'est lui qui
// fait la réactivité perçue en conversation hands-free. D'où deux seuils :
//   - premier segment du tour : court, on parle vite ;
//   - suivants : longs, on regroupe les phrases courtes en blocs qui ont une
//     vraie intonation. L'agent écoute déjà, la latence ne se voit plus.

/** Seuil du tout premier segment d'un tour : priorité à la réactivité. */
export const FIRST_SEGMENT_MIN_CHARS = 20;
/** Seuil des segments suivants : priorité à la ligne mélodique. */
export const NEXT_SEGMENT_MIN_CHARS = 90;

const SENTENCE_TERMINATORS = ".!?…\n";

export interface ExtractResult {
  /** Segments prêts à être synthétisés, dans l'ordre. */
  ready: string[];
  /** Reste non terminé, à conserver pour le prochain appel. */
  rest: string;
}

/**
 * Extrait les segments complets du buffer de streaming.
 *
 * Un segment se termine sur un terminateur (. ! ? … ou saut de ligne) ET doit
 * atteindre la longueur minimale — sinon on continue d'accumuler, ce qui
 * regroupe naturellement les phrases courtes et évite de couper sur une
 * abréviation (« M. Dupont », « 3 p. »).
 *
 * @param isFirst true tant qu'aucun segment n'a encore été émis pour ce tour.
 */
export function extractSentences(buf: string, isFirst: boolean): ExtractResult {
  const ready: string[] = [];
  let start = 0;
  for (let i = 0; i < buf.length; i++) {
    if (!SENTENCE_TERMINATORS.includes(buf[i])) continue;
    // Absorbe les terminateurs consécutifs ("?!", "...").
    let j = i;
    while (j + 1 < buf.length && SENTENCE_TERMINATORS.includes(buf[j + 1])) j++;
    const candidate = buf.slice(start, j + 1).trim();
    // Le seuil se relâche dès qu'un segment est parti : seul le premier son
    // doit arriver vite.
    const min =
      isFirst && ready.length === 0 ? FIRST_SEGMENT_MIN_CHARS : NEXT_SEGMENT_MIN_CHARS;
    if (candidate.length >= min) {
      ready.push(candidate);
      start = j + 1;
    }
    i = j;
  }
  return { ready, rest: buf.slice(start) };
}
